import { User } from '@gbhem/api';
import { ReactNode } from 'react';
import { Redirect, Route, RouteComponentProps, RouteProps } from 'react-router-dom';
import { useAsync } from 'react-use';

import { useAuthenticatedUser } from '../../providers/AuthenticationProvider';
import { useAuthenticatedUserAbility } from '../../providers/PermissionProvider';
import { Loader } from '../Loader';

interface ProtectedRouteProps extends RouteProps {
  children: ReactNode;
  /**
   * Function that determines if the user has permission to access this route.
   * This should contain the permission logic specific to the route group.
   *
   * Supports both synchronous and asynchronous permission checks.
   *
   * @param ability - The CASL ability instance to check permissions
   * @param authUser - The authenticated user object
   * @param params - The route parameters (may contain undefined values)
   * @returns true if user has access, false otherwise (can be async)
   */
  hasPermission?: (
    ability: ReturnType<typeof useAuthenticatedUserAbility>,
    authUser: User,
    params: Record<string, string | undefined>
  ) => boolean | Promise<boolean>;
  /**
   * Optional custom component to show when permission is denied.
   * Defaults to redirecting to /tracks
   */
  fallback?: ReactNode;
  /**
   * Optional loading component while checking permissions
   */
  loader?: ReactNode;
}

/**
 * A route component that enforces permission checks before rendering.
 *
 * Usage:
 * <ProtectedRoute
 *   path="/admin"
 *   hasPermission={(ability) => ability.can('update', 'Conference')}
 * >
 *   <AdminComponent />
 * </ProtectedRoute>
 *
 * Advanced usage with async checks:
 * <ProtectedRoute
 *   path="/users/:id"
 *   hasPermission={async (ability, authUser, params) => {
 *     if (authUser.id === params.id) return true;
 *     const result = await SomeService.checkPermission(params.id);
 *     return result.canView;
 *   }}
 * >
 *   <UserProfile />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  hasPermission,
  fallback,
  loader,
  ...routeProps
}: ProtectedRouteProps) {
  const ability = useAuthenticatedUserAbility();
  const authUser = useAuthenticatedUser();

  return (
    <Route
      {...routeProps}
      render={(props: RouteComponentProps<Record<string, string | undefined>>) => (
        <ProtectedRouteContent
          ability={ability}
          authUser={authUser}
          params={props.match.params}
          hasPermission={hasPermission}
          fallback={fallback}
          loader={loader}
        >
          {children}
        </ProtectedRouteContent>
      )}
    />
  );
}

/**
 * Internal component that handles permission checking after route matching
 */
function ProtectedRouteContent({
  children,
  ability,
  authUser,
  params,
  hasPermission,
  fallback,
  loader
}: {
  children: ReactNode;
  ability: ReturnType<typeof useAuthenticatedUserAbility>;
  authUser: User;
  params: Record<string, string | undefined>;
  hasPermission?: (
    ability: ReturnType<typeof useAuthenticatedUserAbility>,
    authUser: User,
    params: Record<string, string | undefined>
  ) => boolean | Promise<boolean>;
  fallback?: ReactNode;
  loader?: ReactNode;
}) {
  // Use useAsync to handle both sync and async permission checks
  const { loading, value: hasAccess } = useAsync(async () => {
    // If no permission check is provided, allow access
    // This allows for gradual migration and explicit TODOs
    if (!hasPermission) return true;

    // Execute permission check (handles both sync and async)
    return await Promise.resolve(hasPermission(ability, authUser, params));
  }, [ability, authUser, params, hasPermission]);

  // Show loader while checking async permissions
  if (loading) {
    return loader ? <>{loader}</> : <Loader />;
  }

  return <>{hasAccess ? children : fallback ? fallback : <Redirect to="/tracks" />}</>;
}

export default ProtectedRoute;
